import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, FileText } from 'lucide-react';
import DefaultFirstPageEditor from '../components/DefaultFirstPageEditor';
import { apiService } from '../services/api';

const DefaultFirstPageEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [defaultFirstPageName, setDefaultFirstPageName] = useState('');
  const [defaultFirstPageNameError, setDefaultFirstPageNameError] = useState('');
  const [defaultFirstPageContent, setDefaultFirstPageContent] = useState('');
  const [defaultFirstPageActive, setDefaultFirstPageActive] = useState(false);

  const { data: defaultFirstPageData } = useQuery({
    queryKey: ['defaultFirstPage'],
    queryFn: async () => {
      const response = await apiService.getDefaultFirstPage();
      return response.data;
    }
  });

  const { data: companyData } = useQuery({
    queryKey: ['company'],
    queryFn: async () => {
      const response = await apiService.getCompany();
      return response.data;
    }
  });

  const getFullLogoUrl = (logoPath: string | null | undefined): string => {
    if (!logoPath) return '';
    if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) return logoPath;
    const backendBaseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || `${window.location.protocol}//${window.location.hostname}:5269`;
    return `${backendBaseUrl}/api/companies/logo/${logoPath}`;
  };

  useEffect(() => {
    if (!defaultFirstPageData) return;
    setDefaultFirstPageName(defaultFirstPageData.name || '');
    const htmlContent = defaultFirstPageData.htmlContent || '';
    const jsonMatch = htmlContent.match(/<!-- JSON_DATA:([\s\S]*?) -->/);
    const jsonContent = jsonMatch ? jsonMatch[1] : '';
    setDefaultFirstPageContent(jsonContent || htmlContent);
    setDefaultFirstPageActive(defaultFirstPageData.isActive || false);
    setDefaultFirstPageNameError('');
  }, [defaultFirstPageData]);

  const saveDefaultFirstPageMutation = useMutation({
    mutationFn: async (data: any) => apiService.updateDefaultFirstPage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['defaultFirstPage'] });
      navigate('/workorders/templates');
    }
  });

  const replacePlaceholders = (content: string): string => {
    const placeholderMap: Record<string, string> = {
      '{{CompanyName}}': companyData?.name || 'Sample Company Name',
      '{{CompanyAddress}}': companyData?.address || '123 Sample Street',
      '{{CompanyCity}}': companyData?.city || 'Sample City',
      '{{CompanyState}}': companyData?.state || 'OH',
      '{{CompanyZip}}': companyData?.zip || '12345',
      '{{CompanyPhone}}': companyData?.phone || '(555) 123-4567',
      '{{CompanyEmail}}': companyData?.email || 'info@samplecompany.com',
      '{{CompanyWebsite}}': companyData?.website || 'www.samplecompany.com',
      '{{CompanyLogo}}': getFullLogoUrl(companyData?.logoUrl) || '{{CompanyLogo}}',
      '{{WorkOrderNumber}}': 'WO-2024-001',
      '{{WorkOrderPriority}}': 'High',
      '{{WorkOrderStatus}}': 'Open',
      '{{WorkOrderCategory}}': 'Maintenance',
      '{{WorkOrderDescription}}': 'Sample work order description',
      '{{WorkOrderCreatedDate}}': '01/15/2024',
      '{{WorkOrderDueDate}}': '01/20/2024',
      '{{WorkOrderNTE}}': '$500.00',
      '{{ClientName}}': 'Sample Client Inc.',
      '{{ClientAddress}}': '456 Client Avenue',
      '{{ClientCity}}': 'Client City',
      '{{ClientState}}': 'CA',
      '{{ClientZip}}': '90210',
      '{{ClientPhone}}': '(555) 987-6543',
      '{{ClientEmail}}': 'contact@client.com',
      '{{AssignedUserName}}': 'John Smith',
      '{{AssignedUserEmail}}': 'john.smith@company.com',
      '{{AssignedUserPhone}}': '(555) 456-7890'
    };
    let replacedContent = content;
    Object.entries(placeholderMap).forEach(([placeholder, value]) => {
      replacedContent = replacedContent.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
    });
    return replacedContent;
  };

  const convertBlocksToHtml = (blocksJson: string): string => {
    try {
      const blocks = JSON.parse(blocksJson);
      if (!Array.isArray(blocks)) return '';
      return blocks.map((block: any) => {
        const { type, properties, content } = block;
        switch (type) {
          case 'text': {
            const textContent = content.includes('<') ? content : content.replace(/\n/g, '<br>');
            const replacedTextContent = replacePlaceholders(textContent);
            return `<div style="font-size: ${properties.fontSize === 'small' ? '14px' : properties.fontSize === 'medium' ? '16px' : properties.fontSize === 'large' ? '18px' : '20px'}; font-weight: ${properties.fontWeight}; text-align: ${properties.textAlign}; color: ${properties.color};">${replacedTextContent}</div>`;
          }
          case 'logo': {
            const logoUrl = getFullLogoUrl(companyData?.logoUrl) || '{{CompanyLogo}}';
            return `<div style="text-align: center; margin: 20px 0;"><img src="${logoUrl}" alt="Company Logo" style="width: ${properties.width}px; height: ${properties.height}px; max-width: none; max-height: none;" /></div>`;
          }
          case 'logoAddress': {
            const replacedLogoContent = replacePlaceholders(content);
            return `<div style="display: flex; align-items: flex-start; gap: 16px; margin: 20px 0;">
              <div style="width: ${properties.logoWidth}px; height: ${properties.logoHeight}px; background-image: url('${getFullLogoUrl(companyData?.logoUrl) || '{{CompanyLogo}}'}'); background-size: contain; background-repeat: no-repeat; background-position: center; flex-shrink: 0;"></div>
              <div style="flex: 1; font-size: ${properties.fontSize === 'small' ? '14px' : properties.fontSize === 'medium' ? '16px' : properties.fontSize === 'large' ? '18px' : '20px'}; font-weight: ${properties.fontWeight}; line-height: 1.4;">
                <pre style="white-space: pre-wrap; font-family: inherit; margin: 0;">${replacedLogoContent}</pre>
              </div>
            </div>`;
          }
          case 'companyName': {
            const replacedCompanyName = replacePlaceholders(content);
            return `<h1 style="font-size: ${properties.fontSize === 'small' ? '18px' : properties.fontSize === 'medium' ? '24px' : properties.fontSize === 'large' ? '32px' : '40px'}; font-weight: ${properties.fontWeight}; text-align: ${properties.textAlign}; margin: 20px 0;">${replacedCompanyName}</h1>`;
          }
          case 'companyAddress':
          case 'companyPhone':
          case 'companyEmail':
          case 'companyWebsite': {
            const replacedOtherContent = replacePlaceholders(content);
            return `<p style="font-size: ${properties.fontSize === 'small' ? '14px' : properties.fontSize === 'medium' ? '16px' : properties.fontSize === 'large' ? '18px' : '20px'}; font-weight: ${properties.fontWeight}; text-align: ${properties.textAlign}; margin: 10px 0;">
              <pre style="white-space: pre-wrap; font-family: inherit; margin: 0;">${replacedOtherContent}</pre>
            </p>`;
          }
          default:
            return `<p>${content}</p>`;
        }
      }).join('');
    } catch (e) {
      return '';
    }
  };

  const handleSave = () => {
    // Validate template name
    if (!defaultFirstPageName || !defaultFirstPageName.trim()) {
      setDefaultFirstPageNameError('Template name is required');
      return;
    }
    
    setDefaultFirstPageNameError('');
    const isValidJson = (() => { try { JSON.parse(defaultFirstPageContent); return true; } catch { return false; }})();
    const htmlContent = isValidJson ? convertBlocksToHtml(defaultFirstPageContent) : defaultFirstPageContent;
    const htmlWithJson = isValidJson ? `<!-- JSON_DATA:${defaultFirstPageContent} -->\n${htmlContent}` : htmlContent;
    const dataToSave = {
      name: defaultFirstPageName.trim(),
      htmlContent: htmlWithJson,
      isActive: defaultFirstPageActive
    };
    saveDefaultFirstPageMutation.mutate(dataToSave);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <FileText className="h-8 w-8 text-gray-600 dark:text-gray-400" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Default First Page</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Configure the page shown before all work orders</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSave}
            className="flex items-center gap-2 bg-accent-color text-white px-4 py-2 rounded-lg hover:bg-accent-color-600"
          >
            <Save className="w-4 h-4" /> Save
          </button>
          <button
            onClick={() => navigate('/workorders/templates')}
            className="btn-secondary"
          >
            Back
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Template Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={defaultFirstPageName}
            onChange={(e) => {
              setDefaultFirstPageName(e.target.value);
              if (defaultFirstPageNameError) {
                setDefaultFirstPageNameError('');
              }
            }}
            className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
              defaultFirstPageNameError 
                ? 'border-red-500 dark:border-red-500' 
                : 'border-gray-300 dark:border-gray-600'
            }`}
            placeholder="Enter template name"
          />
          {defaultFirstPageNameError && (
            <p className="text-red-500 text-sm mt-1">{defaultFirstPageNameError}</p>
          )}
        </div>

        {/* Enable toggle moved to the top for visibility */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="defaultFirstPageActive"
            checked={defaultFirstPageActive}
            onChange={(e) => setDefaultFirstPageActive(e.target.checked)}
            className="rounded"
          />
          <label htmlFor="defaultFirstPageActive" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Enable this default first page
          </label>
        </div>

        <div className="h-[600px]">
          <DefaultFirstPageEditor
            key={defaultFirstPageContent}
            initialContent={defaultFirstPageContent}
            onSave={(content) => setDefaultFirstPageContent(content)}
            companyData={{
              name: companyData?.name,
              address: companyData?.address,
              city: companyData?.city,
              state: companyData?.state,
              zip: companyData?.zip,
              phone: companyData?.phone,
              email: companyData?.email,
              website: companyData?.website,
              logoUrl: getFullLogoUrl(companyData?.logoUrl)
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DefaultFirstPageEditorPage;


